import {
  CostCodeSearchFields,
  CostCodesPickerQueryQuery,
  CostCodesPickerQueryQueryVariables,
  SearchType,
} from '__generated__/graphql';
import { costCodesQuery } from 'components/domain/cost-code/CostCodePicker/cost-code-picker-queries';
import { useOrganization } from 'hooks';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import { useCallback } from 'react';
import { NonNullArrayField } from 'types/util/NonNullArrayField';

export type CostCodePickerItem = NonNullArrayField<CostCodesPickerQueryQuery['costCodes']>;

export interface UseCostCodePickerDataOptions {
  projectId?: string | null;
  considerProjectSpecificCostCodes?: boolean;
  searchArchived?: boolean;
}

export function useCostCodePickerData({
  projectId,
  considerProjectSpecificCostCodes = true,
  searchArchived = false,
}: UseCostCodePickerDataOptions = {}) {
  const organization = useOrganization();
  const graphqlClient = useGraphQLClient();

  const fetchPage = useCallback(
    async (after: string | null, first: number, searchDebounce: string): Promise<CostCodePickerItem[]> => {
      if (considerProjectSpecificCostCodes && organization.useProjectCostCodeScoping && !projectId) {
        return [];
      }

      const result = await graphqlClient.request<CostCodesPickerQueryQuery, CostCodesPickerQueryQueryVariables>({
        document: costCodesQuery,
        variables: {
          after,
          first,
          filter: {
            archivedOn: searchArchived ? { isNull: false } : { isNull: true },
            search: searchDebounce
              ? {
                  type: SearchType.Contains,
                  fields: [CostCodeSearchFields.CostCode, CostCodeSearchFields.Title],
                  value: searchDebounce,
                }
              : undefined,
            projectCostCodeLink:
              considerProjectSpecificCostCodes && organization.useProjectCostCodeScoping
                ? { projectId: { equal: projectId }, deletedOn: { isNull: true } }
                : undefined,
          },
        },
      });

      return result.costCodes ?? [];
    },
    [graphqlClient, organization, considerProjectSpecificCostCodes, projectId, searchArchived]
  );

  const fetchSelected = useCallback(
    async (ids: string[]): Promise<CostCodePickerItem[]> => {
      const result = await graphqlClient.request<CostCodesPickerQueryQuery, CostCodesPickerQueryQueryVariables>({
        document: costCodesQuery,
        variables: { filter: { id: { contains: ids } } },
      });

      return result.costCodes ?? [];
    },
    [graphqlClient]
  );

  return { fetchPage, fetchSelected };
}
